import { Component, OnInit, Input } from '@angular/core';
import { ModalController } from '@ionic/angular';

export interface Department {
  id: number | string;
  name: string;
}

@Component({
  selector: 'app-dept-selection-modal',
  standalone: false,
  templateUrl: './dept-selection-modal.component.html',
  styleUrls: ['./dept-selection-modal.component.scss'],
})
export class DeptSelectionModalComponent implements OnInit {

  @Input() allDepartments: Department[] = [];
  @Input() selectedDepartmentIds: string[] = [];

  // Internal state
  searchTerm: string = '';
  selectedIds: Set<string> = new Set();
  filteredDepartments: Department[] = [];

  constructor(private modalController: ModalController) { }

  ngOnInit() {
    // Initialize selected IDs set
    this.selectedIds = new Set(this.selectedDepartmentIds.map(id => String(id)));

    // Initialize filtered departments
    this.filteredDepartments = [...this.allDepartments];

    console.log('Modal initialized with:', {
      total: this.allDepartments.length,
      selected: this.selectedIds.size
    });
  }

  /**
   * Filter departments based on search term
   */
  filterDepartments(): void {
    const term = this.searchTerm.toLowerCase().trim();

    if (!term) {
      this.filteredDepartments = [...this.allDepartments];
      return;
    }

    this.filteredDepartments = this.allDepartments.filter(dept =>
      dept.name.toLowerCase().includes(term)
    );
  }

  /**
   * Get selected departments
   */
  getSelectedDepartments(): Department[] {
    return this.allDepartments.filter(dept =>
      this.selectedIds.has(String(dept.id))
    );
  }

  /**
   * Get available (non-selected) departments from filtered list
   */
  getAvailableDepartments(): Department[] {
    return this.filteredDepartments.filter(dept =>
      !this.selectedIds.has(String(dept.id))
    );
  }

  /**
   * Toggle department selection
   */
  toggleDepartment(dept: Department, event?: any): void {
    const deptId = String(dept.id);

    if (this.selectedIds.has(deptId)) {
      this.selectedIds.delete(deptId);
    } else {
      this.selectedIds.add(deptId);
    }
  }

  /**
   * Check if department is selected
   */
  isSelected(dept: Department): boolean {
    return this.selectedIds.has(String(dept.id));
  }

  /**
   * Select all filtered departments
   */
  selectAll(): void {
    this.filteredDepartments.forEach(dept => {
      this.selectedIds.add(String(dept.id));
    });
  }

  /**
   * Clear all selections
   */
  clearAll(): void {
    this.selectedIds.clear();
  }

  /**
   * Highlight matching text in department name
   */
  highlightMatch(name: string): string {
    if (!this.searchTerm) {
      return name;
    }

    const term = this.searchTerm.trim();
    const regex = new RegExp(`(${term})`, 'gi');
    return name.replace(regex, '<mark>$1</mark>');
  }

  /**
   * Confirm selection and close modal
   */
  confirm(): void {
    const selectedIds = Array.from(this.selectedIds);
    this.modalController.dismiss({
      selectedIds: selectedIds
    }, 'confirm');
  }

  /**
   * Cancel and close modal
   */
  dismiss(): void {
    this.modalController.dismiss(null, 'cancel');
  }

  /**
   * Get count of selected departments
   */
  get selectedCount(): number {
    return this.selectedIds.size;
  }

  /**
   * Get count of available (filtered) departments
   */
  get availableCount(): number {
    return this.getAvailableDepartments().length;
  }

  /**
   * Check if there are any filtered results
   */
  get hasFilteredResults(): boolean {
    return this.filteredDepartments.length > 0;
  }

  /**
   * Check if search returned no results
   */
  get noSearchResults(): boolean {
    return this.searchTerm.length > 0 && this.filteredDepartments.length === 0;
  }

}
